import { logSwallowed } from '@/lib/errors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListChecks, ClipboardList, Bell, FileText, MousePointerClick, Inbox, Table2, Layers } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useWorktableContext } from '@/lib/worktableContext';
import { eventLog } from '@/lib/eventLog';
import { AutoListForm } from '@/components/autoforms/AutoListForm';
import { AutoObjectForm } from '@/components/autoforms/AutoObjectForm';
import { FilledPollsPage } from '@/pages/FilledPollsPage';
import { ObjectCard, type CardField } from '@/components/ui/ObjectCard';
import { CalendarWidget } from '@/components/widgets/CalendarWidget';
import { CardGrid } from '@/components/ui/CardGrid';
import { Button } from '@/components/ui/button';
import { registerWidget, listWidgets, type WidgetContext, type WidgetProps } from './registry';
import { registerSiteBlocks } from './blocks';
import { registerSiteMarketplaceBlocks } from './blocks-marketplace';
import { SitePageRecords, siteQueryFilters } from './sitePageRecords';
import type { WorktableWidget } from './gridModel';
import { friendlyError } from '@/lib/errors';
import type { ListFilter } from '@/types/formProperties';
import { resolveDlFilters } from '@/lib/dlFilters';
import { ChatThread } from '@/components/chat/ChatThread';
import { TabBar } from '@/components/ui/TabBar';
import { useBreakpoint } from '@/hooks/useBreakpoint';

export function WidgetShell({ title, children, icon, showTitle = true, showBorder = true }: { title?: string; children: React.ReactNode; icon?: React.ReactNode; showTitle?: boolean; showBorder?: boolean }) {
  return (
    <div className={`flex h-full flex-col rounded-xl bg-card shadow-sm overflow-hidden ${showBorder ? 'border' : ''}`}>
      {showTitle && title && (
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 text-sm font-medium shrink-0">
          {icon}
          <span className="truncate">{title}</span>
        </div>
      )}
      {/* flex flex-col — чтобы встроенные списки/таблицы (flex-1) растягивались на всю область виджета */}
      <div className="p-2 flex-1 min-h-0 overflow-auto flex flex-col">{children}</div>
    </div>
  );
}

export function EmptyState({ text }: { text: string }) {
  return <div className="text-xs text-muted-foreground p-3">{text}</div>;
}

// --- «Мои задачи» (tasks_mine) ------------------------------------------------

export function TasksMineWidget(props: WidgetProps, ctx: WidgetContext) {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r: any = await getSDK().task.listTasks({
          filter: 'mine',
          // includeDone=false (ПО УМОЛЧАНИЮ): выполненные/отменённые задачи
          // (конечные статусы схемы) в виджете НЕ показываются.
          includeDone: false,
          pageSize: props.limit || 8,
        } as any);
        if (!cancelled) setItems((r as any)?.items || []);
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.limit]);

  const open = (t: any) => {
    if (ctx.onObjectOpen) ctx.onObjectOpen('task', t?.taskType || t?.task_type || t?.type || '', t?.id);
    else navigate('/designer/tasks');
  };

  if (loading) return <WidgetShell title={props.title || 'Мои задачи'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-muted-foreground p-3">Загрузка…</div></WidgetShell>;
  if (error) return <WidgetShell title={props.title || 'Мои задачи'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-destructive p-3">{error}</div></WidgetShell>;
  if (items.length === 0) return <WidgetShell title={props.title || 'Мои задачи'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<ListChecks className="h-4 w-4" />}><EmptyState text="Задач нет" /></WidgetShell>;

  const fields: CardField[] = [
    { name: 'name', synonym: 'Тема', dataType: 'text', importance: 5 },
    { name: 'status_code', synonym: 'Статус', dataType: 'enumeration', importance: 5 },
    { name: 'assignee_id', synonym: 'Исполнитель', dataType: 'reference', importance: 4 },
    { name: 'due_date', synonym: 'Срок', dataType: 'date', importance: 4 },
  ];

  return (
    <WidgetShell title={props.title || 'Мои задачи'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<ListChecks className="h-4 w-4" />}>
      <div className="space-y-1">
        {items.map((t: any) => (
          <ObjectCard
            key={t.id}
            fields={fields}
            data={{ ...t, status_code: t.statusCode || '', priority: String(t.priority || '2') }}
            presentations={{ assignee_id_presentation: t.assigneeName || '' }}
            variant="plain"
            className="hover:bg-accent/40 cursor-pointer"
            onClick={() => open(t)}
          />
        ))}
      </div>
    </WidgetShell>
  );
}

// --- «Мои заявки» (orders_mine) ------------------------------------------------

export function OrdersMineWidget(props: WidgetProps, ctx: WidgetContext) {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r: any = await getSDK().order.listOrders({ includeDone: false, pageSize: props.limit || 8 } as any);
        if (!cancelled) setItems((r as any)?.items || []);
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.limit]);

  if (loading) return <WidgetShell title={props.title || 'Мои заявки'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-muted-foreground p-3">Загрузка…</div></WidgetShell>;
  if (error) return <WidgetShell title={props.title || 'Мои заявки'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-destructive p-3">{error}</div></WidgetShell>;
  if (items.length === 0) return <WidgetShell title={props.title || 'Мои заявки'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<ClipboardList className="h-4 w-4" />}><EmptyState text="Заявок нет" /></WidgetShell>;

  return (
    <WidgetShell title={props.title || 'Мои заявки'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<ClipboardList className="h-4 w-4" />}>
      <div className="space-y-1">
        {items.map((o: any) => (
          <ObjectCard
            key={o.id}
            fields={[
              { name: 'name', synonym: 'Тема', dataType: 'text', importance: 5 },
              { name: 'status_name', synonym: 'Статус', dataType: 'text', importance: 5 },
              { name: 'number', synonym: 'Номер', dataType: 'text', importance: 4 },
            ]}
            data={{ ...(o.extraFields || {}), ...o, status_name: o.statusName || '' }}
            variant="plain"
            className="hover:bg-accent/40 cursor-pointer"
            onClick={() => { if (ctx.onObjectOpen) ctx.onObjectOpen('order', o?.orderType || o?.order_type || o?.type || '', o.id); else navigate('/designer/orders'); }}
          />
        ))}
      </div>
    </WidgetShell>
  );
}

// --- «Пул задач» (tasks_pool) --------------------------------------------------

export function TasksPoolWidget(props: WidgetProps, ctx: WidgetContext) {
  const navigate = useNavigate();
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r: any = await getSDK().task.listTasks({ filter: 'pool', includeDone: false, pageSize: props.limit || 8 } as any);
        if (!cancelled) setItems((r as any)?.items || []);
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.limit]);

  if (loading) return <WidgetShell title={props.title || 'Пул задач'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-muted-foreground p-3">Загрузка…</div></WidgetShell>;
  if (error) return <WidgetShell title={props.title || 'Пул задач'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-destructive p-3">{error}</div></WidgetShell>;
  if (items.length === 0) return <WidgetShell title={props.title || 'Пул задач'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Inbox className="h-4 w-4" />}><EmptyState text="Пул пуст" /></WidgetShell>;

  return (
    <WidgetShell title={props.title || 'Пул задач'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Inbox className="h-4 w-4" />}>
      <div className="space-y-1">
        {items.map((t: any) => (
          <ObjectCard
            key={t.id}
            fields={[
              { name: 'name', synonym: 'Тема', dataType: 'text', importance: 5 },
              { name: 'status_code', synonym: 'Статус', dataType: 'enumeration', importance: 5 },
            ]}
            data={{ ...t, status_code: t.statusCode || '' }}
            variant="plain"
            className="hover:bg-accent/40 cursor-pointer"
            onClick={() => { if (ctx.onObjectOpen) ctx.onObjectOpen('task', t?.taskType || t?.task_type || t?.type || '', t.id); else navigate('/designer/tasks'); }}
          />
        ))}
      </div>
    </WidgetShell>
  );
}

// --- «Уведомления» (notifications) --------------------------------------------

export function NotificationsWidget(props: WidgetProps) {
  const [items, setItems] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r: any = await getSDK().metadata.listNotifications({});
        const all = (r as any)?.items || [];
        if (!cancelled) setItems(all.slice(0, props.limit || 8));
      } catch (e: any) {
        if (!cancelled) setError(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.limit]);

  if (loading) return <WidgetShell title={props.title || 'Уведомления'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-muted-foreground p-3">Загрузка…</div></WidgetShell>;
  if (error) return <WidgetShell title={props.title || 'Уведомления'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-destructive p-3">{error}</div></WidgetShell>;
  if (items.length === 0) return <WidgetShell title={props.title || 'Уведомления'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Bell className="h-4 w-4" />}><EmptyState text="Уведомлений нет" /></WidgetShell>;

  return (
    <WidgetShell title={props.title || 'Уведомления'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Bell className="h-4 w-4" />}>
      <div className="space-y-1">
        {items.map((n: any) => (
          <div key={n.id} className="px-2 py-1.5 border-b last:border-0 text-xs">
            <div className={`font-medium ${n.isRead ? '' : 'text-primary'}`}>{n.title}</div>
            {n.message && <div className="text-muted-foreground line-clamp-2">{n.message}</div>}
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

// --- «Быстрые действия» (quick_actions) ----------------------------------------

export function QuickActionsWidget(props: WidgetProps, ctx: WidgetContext) {
  const navigate = useNavigate();
  // actions могут быть массивом ИЛИ текстом «label;команда» построчно (из редактора).
  const actions = (() => {
    if (Array.isArray(props.actions) && props.actions.length) return props.actions;
    if (typeof props.actions === 'string' && props.actions.trim()) {
      return props.actions.split('\n').map((line) => {
        const [label, command = ''] = line.split(';').map((s) => s.trim());
        return { label: label || command, command };
      }).filter((a) => a.label);
    }
    return [];
  })();
  return (
    <WidgetShell title={props.title || 'Быстрые действия'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<MousePointerClick className="h-4 w-4" />}>
      <div className="flex flex-wrap gap-2 p-1">
        {actions.length === 0 && <EmptyState text="Действия не настроены" />}
        {actions.map((a, i) => (
          <Button
            key={i}
            size="sm"
            variant={((a as any).variant || 'outline') as any}
            className="h-8 text-xs"
            onClick={() => {
              if (a.command.startsWith('navigate:')) navigate(a.command.slice('navigate:'.length));
              else if (ctx.onObjectOpen) ctx.onObjectOpen('', '');
            }}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </WidgetShell>
  );
}

// --- Текст (text) ---------------------------------------------------------------

export function TextWidget(props: WidgetProps) {
  return (
    <WidgetShell title={props.title} showTitle={props.showTitle} showBorder={props.showBorder}>
      <div className="text-sm text-foreground p-1 whitespace-pre-wrap">{props.text || ''}</div>
    </WidgetShell>
  );
}

// --- HTML (html) ----------------------------------------------------------------
// SANDBOX-РЕНДЕР (Этап 1 сайтов): HTML выполняется в изолированном iframe
// (sandbox="" — без скриптов/форм/навигации; srcdoc). Раньше был
// dangerouslySetInnerHTML — XSS/порча темы платформы.
export function HtmlWidget(props: WidgetProps) {
  return (
    <WidgetShell title={props.title} showTitle={props.showTitle} showBorder={props.showBorder}>
      <iframe
        title={String(props.title || 'HTML-блок')}
        sandbox=""
        srcDoc={String(props.text || '')}
        className="w-full flex-1 min-h-[80px] border-0 bg-transparent"
      />
    </WidgetShell>
  );
}

// --- «Форма списка» (list) ------------------------------------------------------
// Рендерит СТАНДАРТНУЮ форму списка (AutoListForm) произвольного объекта
// (справочник/документ/заявка/регистр) с КАСТОМИЗИРУЕМЫМИ отборами и видом.
// ОТБОРЫ поддерживают параметры:
//   @current_user         — id текущего пользователя
//   @context.<имя>        — параметр КОНТЕКСТА рабочего стола (master→slave)
//   @param.<имя>          — параметр ВИДЖЕТА (локальный)
// MASTER: если задан contextParam — клик по строке кладёт поле строки
// (contextField, по умолчанию id) в параметр контекста → «slave»-виджеты
// с @context.<имя> обновляются автоматически. Двойной клик — открыть объект.
export function ListFormWidget(props: WidgetProps, ctx: WidgetContext) {
  const objectType = String(props.objectType || 'catalog');
  const objectName = String(props.objectName || '');
  // «Создать»: открывает форму объекта ИСТОЧНИКА (новая запись) — как в 1С.
  // AutoObjectForm сам рендерит окно (UniversalWindow) — передаём objectId=''.
  const [createOpen, setCreateOpen] = useState(false);
  // Параметры контекста рабочего стола (при изменении — отборы пересчитываются).
  const ctxParams = useWorktableContext((s) => s.params);
  const meUserId = useAuthStore((s) => s.user?.id || '');
  // ХВОСТЫ ЭТАПОВ 2–3: СТРАНИЦА-СПИСОК объекта (object_mode='list') и
  // query-отборы из URL. props.pageRecords → блок рендерит СЕРВЕРНЫЙ постраничный
  // результат ctx.pageList (SSoT — GetPublicPage.records), не свой источник;
  // props.queryFields (CSV) — whitelist query-параметров для СЕРВЕРНЫХ отборов
  // (ListTableData.filters — фильтрует сервер, не клиентский applyFilters).
  const [searchParams] = useSearchParams();
  const queryFieldsCsv = String(props.queryFields || '');
  const queryFilters = useMemo(
    () => siteQueryFilters(searchParams, queryFieldsCsv),
    [searchParams, queryFieldsCsv],
  );
  const pageRecords = props.pageRecords === true;

  // ДИАГНОСТИКА (пока не разберёмся с «вид не меняется»): что получает виджет.
  useEffect(() => {
    eventLog.info('ui', 'list-виджет: viewMode=' + String(props.viewMode), {
      details: 'type=' + objectType + ' name=' + objectName + ' title=' + String(props.showTitle) + ' border=' + String(props.showBorder),
    }).catch((e) => { logSwallowed(e, 'lib/widgets/builtinWidgets.tsx:357'); return undefined; });
  }, [props.viewMode, props.showTitle, props.showBorder, objectType, objectName]);

  // Отборы — ЕДИНЫЙ резолвер (lib/dlFilters) СИНХРОННО на рендере (SSoT/SRP):
  // список сразу получает развёрнутые привязки @current_user/@context/@param и
  // признак «заведомо пуст» (незаполненная привязка ⇒ empty, НЕ «все записи»).
  // Синхронно — чтобы первый же кадр был детерминированным (эффект родителя
  // выполняется ПОСЛЕ эффектов ребёнка — список успевал бы уйти в выборку
  // «без отбора»).
  const { resolvedFilters, forcedEmpty, filterError } = useMemo(() => {
    try {
      const raw = typeof props.filters === 'string' && props.filters.trim()
        ? JSON.parse(props.filters)
        : (Array.isArray(props.filters) ? props.filters : []);
      const filters = Array.isArray(raw) ? (raw as ListFilter[]) : [];
      const wparams = (props.params && typeof props.params === 'object') ? props.params : {};
      const { filters: resolved, empty } = resolveDlFilters(filters, {
        userId: meUserId,
        params: wparams as Record<string, string>,
        contextParams: (ctxParams || {}) as Record<string, string>,
      });
      return { resolvedFilters: resolved as ListFilter[], forcedEmpty: empty, filterError: null as string | null };
    } catch {
      return { resolvedFilters: [] as ListFilter[], forcedEmpty: true, filterError: 'Невалидные отборы' };
    }
  }, [props.filters, props.params, ctxParams, meUserId]);

  // MASTER: клик по строке — установить параметр контекста (для slave-виджетов).
  const onSelect = (row: Record<string, unknown>) => {
    if (props.contextParam) {
      const field = props.contextField || 'id';
      useWorktableContext.getState().setParam(props.contextParam, String(row[field] ?? ''));
    }
  };

  const openRow = (row: Record<string, unknown>) => {
    const id = String(row['id'] ?? '');
    if (id && ctx.onObjectOpen) ctx.onObjectOpen(objectType, objectName, id);
  };

  // ТЕСТ/АНКЕТА (objectType='poll') — ЕДИНЫЙ компонент теста (вопросы методики +
  // «Пройти» + протоколы прохождения), а НЕ AutoListForm/автоформа: у типа опроса
  // нет пользовательских реквизитов (автоформа показывала «Нет реквизитов в
  // метаданных»), а данные лежат в физтаблице {code}. objectName пуст → список
  // методик с выбором (тот же компонент, что в SectionPage и конфигураторе).
  if (objectType.toLowerCase() === 'poll') {
    return (
      <WidgetShell title={props.title || 'Тесты и анкеты'} showTitle={props.showTitle} showBorder={props.showBorder} icon={<ClipboardList className="h-4 w-4" />}>
        <FilledPollsPage code={objectName || undefined} />
      </WidgetShell>
    );
  }

  return (
    <>
    <WidgetShell title={props.title} showTitle={props.showTitle} showBorder={props.showBorder} icon={<Table2 className="h-4 w-4" />}>
      {!!props.listTitle && (
        <div className="px-3 py-1.5 text-[11px] font-medium border-b bg-muted/20 shrink-0">{props.title || objectName}</div>
      )}
      <div className={`flex-1 min-h-0 flex flex-col ${(props as any).listBorder === false ? '' : 'border rounded-lg'}`}>
        {filterError ? (
          <EmptyState text={filterError} />
        ) : pageRecords ? (
          ctx.pageList ? (
            <SitePageRecords
              list={ctx.pageList}
              itemUrlTemplate={String(props.itemUrl || '')}
              viewMode={String(props.viewMode || 'list')}
              siteCode={ctx.siteCode || ''}
              locale={ctx.siteLocale || ''}
              defaultLocale={ctx.siteDefaultLocale || 'ru'}
              title={props.title}
            />
          ) : (
            <EmptyState text="Блок «Записи страницы-списка» работает только на странице объекта с режимом «Список»" />
          )
        ) : (
          <AutoListForm
            objectType={objectType}
            objectName={objectName}
            filters={resolvedFilters}
            forceEmpty={forcedEmpty}
            serverFilters={queryFilters.length > 0 ? queryFilters : undefined}
            viewMode={props.viewMode || 'list'}
            groupMode={String(props.groupMode || '')}
            groupBy={String(props.groupBy || '')}
            showToolbar={props.showToolbar === true}
            commands={Array.isArray(props.commands) ? props.commands : undefined}
            singleClickOpen
            // «Создать» — ЕДИНЫЙ путь через ctx.onObjectOpen(id='') → форма
            // объекта открывается state-экраном в рабочей области (Предприятие),
            // а не модалкой поверх (раньше AutoObjectForm рендерился прямо здесь).
            onCreate={() => setCreateOpen(true)}
            onSelect={props.contextParam ? onSelect : undefined}
            onRowDoubleClick={openRow}
          />
        )}
      </div>
    </WidgetShell>
    {createOpen && (
      <AutoObjectForm
        objectType={objectType}
        objectName={objectName}
        objectId=""
        onClose={() => setCreateOpen(false)}
      />
    )}
    </>
  );
}

// --- «Контекст» (context) --------------------------------------------------------
// Редактирует параметр КОНТЕКСТА рабочего стола (поле ввода). Значение попадает
// в @context.<имя> → ВСЕ виджеты с таким отбором обновляются (master→slave).
// Позволяет «просто ввести значение» и применить его к отборам N виджетов.
export function ContextWidget(props: WidgetProps) {
  const paramName = String(props.param || '');
  const value = useWorktableContext((s) => s.params[paramName] || '');
  const setValue = (v: string) => useWorktableContext.getState().setParam(paramName, v);

  return (
    <WidgetShell title={props.title || (paramName ? `Контекст: ${paramName}` : 'Контекст')} showTitle={props.showTitle} showBorder={props.showBorder} icon={<MousePointerClick className="h-4 w-4" />}>
      <div className="p-1">
        {!paramName ? (
          <EmptyState text="Задайте имя параметра (param)" />
        ) : (
          <>
            <input
              className="w-full h-9 rounded-md border bg-input px-3 text-sm"
              value={value}
              placeholder={(props as any).placeholder || 'значение параметра…'}
              onChange={(e) => setValue(e.target.value)}
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              @context.{paramName} — обновит отборы всех виджетов с этим параметром
            </div>
          </>
        )}
      </div>
    </WidgetShell>
  );
}

// --- «Чат» (chat_thread) — встраиваемый чат как в Telegram --------------------
// Встраивает ChatThread (переписку) прямо в рабочий стол. Если conversationId
// не задан — показывает первый доступный чат. Сохраняет черновик per conv.
export function ChatThreadWidget(props: WidgetProps) {
  const myId = useAuthStore((s: any) => s.user?.id || '');
  const isAdmin = useAuthStore((s: any) => (typeof s.isAdmin === 'function' ? s.isAdmin() : !!s.user?.isAdmin));
  const [convId, setConvId] = useState<string>(String((props as any).conversationId || ''));
  const [convs, setConvs] = useState<Array<{ id: string; title: string }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (convId) return;
    setLoading(true);
    getSDK().messenger.listConversations({} as any).then((r: any) => {
      const items = (r?.items || []) as any[];
      setConvs(items.map((c) => ({ id: c.id, title: c.title || c.id.slice(0, 8) })));
      if (items[0] && !convId) setConvId(items[0].id);
    }).catch((e) => { logSwallowed(e, 'lib/widgets/builtinWidgets.tsx:517'); return undefined; }).finally(() => setLoading(false));
  }, [convId]);

  // Следим за внешним изменением props.conversationId (редактор)
  useEffect(() => {
    const p = String((props as any).conversationId || '');
    if (p && p !== convId) setConvId(p);
  }, [props]);

  if (loading && !convId) return <WidgetShell title={props.title || 'Чат'} showTitle={props.showTitle} showBorder={props.showBorder}><div className="text-xs text-muted-foreground p-3">Загрузка…</div></WidgetShell>;
  if (!convId) return <WidgetShell title={props.title || 'Чат'} showTitle={props.showTitle} showBorder={props.showBorder}><EmptyState text="Чатов нет — создайте в /chat" /></WidgetShell>;
  return (
    <WidgetShell title={props.title || 'Чат'} showTitle={props.showTitle} showBorder={props.showBorder}>
      <div className="flex-1 min-h-0 flex flex-col -m-2">
        <ChatThread conversationId={convId} title={props.title || 'Чат'} myId={myId} isAdmin={!!isAdmin} />
      </div>
    </WidgetShell>
  );
}

// --- Регистрация стандартных виджетов ядра -------------------------------------
